/**
 * The „Co umíme“ section — one list, read by everything that shows it.
 *
 * The header dropdown, the footer column and the ``/features`` hub all
 * name the same six pages. Written out three times they drift: a slug
 * is renamed in the router, the footer keeps the old one, and the only
 * thing that notices is a visitor hitting a 404. So the list is data,
 * and the three surfaces project it.
 *
 * ``ready`` is how this list survives being built by more than one
 * session at once. The slugs were agreed up front — that is what stops
 * two people inventing two names for one page — but the pages land in
 * separate pull requests. An entry that has no route yet is simply not
 * rendered: no dead link, and no "coming soon" tile, which is a promise
 * nobody asked us to make. Shipping a page is then two lines in the
 * same commit: its route in ``PlatformMarketingShell`` and ``ready``
 * here.
 *
 * ⚠️ ``prerender.routes.json`` is NOT generated from this list and
 * cannot be: it is read by a Node build script that cannot import TS.
 * A new ready page has to be added there too, and
 * ``tests/prerenderRoutes.test.ts`` fails when the two disagree.
 */

export interface FeaturePage {
  /** Path without the ``/:lang`` prefix. */
  to: string;
  /** Key under ``saas:features.tiles`` — the tile's title and one-liner. */
  i18nKey: string;
  /** ``data-testid`` on every surface that renders this entry. */
  testId: string;
  /** Does a route exist for ``to`` today? See the note above. */
  ready: boolean;
}

export const FEATURE_PAGES: readonly FeaturePage[] = [
  // Quoting used to point at the landing, on the theory that the whole
  // site already sells it. The 2026-10-07 audit (#61) found the result:
  // the core of the product was the one entry with no page explaining
  // how it works, and the footer column left it out entirely.
  {
    to: "/features/naceneni-a-objednavka",
    i18nKey: "quoting",
    testId: "features-tile-quoting",
    ready: true,
  },
  {
    to: "/features/sklad-a-nakup",
    i18nKey: "warehouse",
    testId: "features-tile-warehouse",
    ready: true,
  },
  {
    to: "/features/vyroba-a-dilna",
    i18nKey: "production",
    testId: "features-tile-production",
    ready: true,
  },
  {
    to: "/features/firemni-zakaznici",
    i18nKey: "b2b",
    testId: "features-tile-b2b",
    ready: true,
  },
  {
    to: "/features/faktury-a-dane",
    i18nKey: "invoicing",
    testId: "features-tile-invoicing",
    ready: true,
  },
  {
    to: "/features/napojeni-a-rozsireni",
    i18nKey: "integrations",
    testId: "features-tile-integrations",
    ready: true,
  },
];

/** The entries that have a route today — what the three surfaces render. */
export function readyFeaturePages(): readonly FeaturePage[] {
  return FEATURE_PAGES.filter((page) => page.ready);
}
